import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextField, List, ListItem, ListItemText, Box, Stack, Card, CardHeader, CardContent, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, MenuItem, Menu, Pagination } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Loading from '../../components/loading';
import api from '../../api/api';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';

export default function PatientList({ onPatientSelect }) {
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [paginationCount, setPaginationCount] = useState(0);
  const [actualPage, setActualPage] = useState(1);

  const [allPatients, setAllPatients] = useState([]);
  const [patientNameOrCpf, setPatientNameOrCpf] = useState('');

  const [showModalToDeletePatient, setShowModalToDeletePatient] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const navigate = useNavigate();

  const getAllPatients = useCallback(() => {
    setLoading(true);
    api
      .get('/patients', {
        headers: {
          Authorization: sessionStorage.getItem('token'),
        },
      })
      .then((returnFromApi) => {
        setPaginationCount(returnFromApi.data.page.totalPages);
        setAllPatients(returnFromApi.data.content);
      })
      .catch((returnFromApi) => {
                setLoading(false);
                console.error("ops! ocorreu um erro" + returnFromApi);
                setErrorMessage(returnFromApi.response.data);
                if (returnFromApi.status === 401) {
                    navigate("/expired-session");
                }  
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  useEffect(() => {
    getAllPatients();
  }, [getAllPatients]);

  function searchPatients(page) {
    setLoading(true);
    api.get('/patients', {
      headers: {
        Authorization: sessionStorage.getItem('token'),
      },
      params: {
        nameOrCpf: patientNameOrCpf || '',
        page: page - 1,
        size: 5,
        sort: 'name,asc'
      }
    }).then((returnFromApi) => {
      setPaginationCount(returnFromApi.data.page.totalPages);
      setAllPatients(returnFromApi.data.content);
    }).catch((returnFromApi) => {
      console.error('Ops! Ocorreu um erro ao buscar pacientes:', returnFromApi);
      if (returnFromApi.status === 401) {
        navigate("/expired-session");
      }
    }).finally(() => {
      setLoading(false);
    });
  }

  const openMenu = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const openModalToDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowModalToDeletePatient(true);
    closeMenu();
  };

  const closeModalToDeletePatient = () => {
    setShowModalToDeletePatient(false);
    setPatientToDelete(null);
    setSelectedPatient(null);
  };

  const deletePatient = () => {
    if (!patientToDelete) return;
    setShowModalToDeletePatient(false);
    setLoading(true);
    api
      .delete(`/patients/${patientToDelete.id}`, {
        headers: {
          Authorization: sessionStorage.getItem('token'),
        },
      })
      .then((returnFromApi) => {
        searchPatients(actualPage);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        console.error('Ops! Ocorreu um erro ao deletar o paciente:', returnFromApi.response);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response.data);
      })
      .finally(() => {
        setLoading(false);
        closeModalToDeletePatient();
      });
  }

  const handleNavigateToCreate = () => {
    navigate('/patient-form');
  };

  const searchPatient = (event) => {
    event.preventDefault();
    searchPatients();
  }

  const changePage = (event, value) => {
    event.preventDefault();
    setActualPage(value);
    searchPatients(value);
  }

  function goToMedicalRecord(patient) {
    // setLoading(true);
    // const BASE_URL_OLD_FRONT = process.env.REACT_APP_OLD_FRONT_URL;
    // const token = sessionStorage.getItem('token');
    // window.location.href = BASE_URL_OLD_FRONT.concat(`/prontuario/paciente/${patient.id}?token=`, token, "&pacienteId=", patient.id);
    navigate(`/patient-form/${patient.id}`);
  }

  const goToGeneralEvolutions = (patient) => {
    navigate(`/patient-form/${patient.id}?tab=evolutions`);
    closeMenu();
  };

  return (
    <Card sx={{ bgcolor: 'white', borderRadius: '1rem', boxShadow: 3 }}>
      <CardHeader
        title={
          <Typography variant="h5" sx={{ mb: 0, fontWeight: 'bold', color: 'text.secondary' }}>
            Pacientes
          </Typography>
        }
        sx={{ bgcolor: 'white', borderBottom: 0, py: 3 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" onClick={handleNavigateToCreate}>
            Novo
          </Button>
        </Box>

        <Typography variant="h6" sx={{ mb: 1 }}>
          Buscar Paciente
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Digite o nome do paciente ou o cpf..."
            value={patientNameOrCpf}
            onChange={(e) => setPatientNameOrCpf(e.target.value)}
          />
          <Button variant="contained" color="primary" onClick={searchPatient}>
            Buscar
          </Button>
        </Stack>

        <Loading isToHidden={!isLoading} />
        {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
        {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

        <List>
          {allPatients.length > 0 ? (
            allPatients.map((patient) => (
              <ListItem
                key={patient.id}
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 1,
                  boxShadow: 1,
                }}>
                <ListItemText primary={patient.name} secondary={`CPF: ${patient.cpf}`} />

                <Box>
                  <Button
                    aria-controls={anchorEl ? 'patient-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={anchorEl ? 'true' : undefined}
                    onClick={(event) => openMenu(event, patient)}
                  >
                    <MoreVertIcon />
                  </Button>
                  <Menu
                    id="patient-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && selectedPatient?.id === patient.id}
                    onClose={closeMenu}
                  >
                    <MenuItem onClick={() => goToMedicalRecord(patient)}>Ir para o Prontuário</MenuItem>
                    <MenuItem onClick={() => goToGeneralEvolutions(patient)}>Ir para Evoluções Gerais</MenuItem>
                    <MenuItem onClick={() => openModalToDeletePatient(patient)}>Excluir</MenuItem>
                  </Menu>
                </Box>
              </ListItem>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography>Nenhum paciente encontrado.</Typography>
            </Box>
          )}
        </List>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={paginationCount}
            page={actualPage}
            onChange={changePage}
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </CardContent>

      <Dialog
        open={showModalToDeletePatient}
        onClose={closeModalToDeletePatient}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar exclusão"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza de que deseja deletar o paciente "{patientToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModalToDeletePatient}>Cancelar</Button>
          <Button onClick={deletePatient} sx={{ color: 'error.main' }}>
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}